import React, { MouseEventHandler } from "react";
import { useTranslation } from 'react-i18next';
import { Destination } from "../common/ObjectTypes";
import { CopyToClipboard, RuleConfiguredIcon, SortingArrows } from "../common/ProjectIcons";
import DeleteButton from "../common/componentstyles/deleteButton";
import DestinationActionsMenu from "../common/DestinationActionsMenu";
import NoResults from "../common/NoResults";
import { tableClasses, buttonClasses, skeletonClasses } from "../common/tailwindClassnames";
import AddDestinationChoiceView from "./AddDestinationChoiceView";
import { toast } from 'react-toastify';

type ConnectionDestinationTableProps = {
    newConnection: boolean,
    destinations: Destination[],
    existingDestPhase0: Destination[],
    existingDestPhase1: Destination[],
    existingDestPhase2: Destination[],
    existingDestPhase3: Destination[],
    addExistingDestinations: boolean,
    updatingDestinations: boolean,
    onAddExistingDestinations: MouseEventHandler,
    onClickAddDestination: Function,
    onViewDetails: Function,
    onRemoveDestination: Function,
    onSelectDestination: Function,
    onOpenRules?: (destination: Destination) => void,
    destinationsWithRules?: Set<string>,
    searchTerm?: string,
    showNewDestination?: boolean,
}

const ConnectionDestinationTable = ({
    newConnection,
    destinations,
    existingDestPhase0,
    existingDestPhase1,
    existingDestPhase2,
    existingDestPhase3,
    addExistingDestinations,
    updatingDestinations,
    onAddExistingDestinations,
    onClickAddDestination,
    onViewDetails,
    onRemoveDestination,
    onSelectDestination,
    onOpenRules,
    destinationsWithRules,
    searchTerm = "",
    showNewDestination = true,
}: ConnectionDestinationTableProps) => {
    const { t } = useTranslation();
    let destlist = addExistingDestinations ? existingDestPhase0 : destinations;

    // Helper: check if any destination in phase 1, 2, or 3 matches the search term (name or base_url)
    const lowerSearch = searchTerm.trim().toLowerCase();
    function matchesSearch(dest: Destination) {
        return (
            dest.name.toLowerCase().includes(lowerSearch) ||
            dest.base_url.toLowerCase().includes(lowerSearch)
        );
    }
    const hasOtherPhaseResultsInSearch =
        !!lowerSearch && ([...existingDestPhase1, ...existingDestPhase2, ...existingDestPhase3].some(matchesSearch));

    // Show choice view when there are no connected destinations (connection details context only)
    const showChoiceView = !newConnection && destinations.length === 0 && !addExistingDestinations;

    if (showChoiceView) {
        return (
            <AddDestinationChoiceView
                onAddExisting={() => onClickAddDestination(true)}
                onCreateNew={showNewDestination ? () => onClickAddDestination(false) : undefined}
            />
        );
    }

    return (
        <div>
            {!addExistingDestinations && <div className="flex flex-row my-3 transition-all duration-300">
                {showNewDestination && <button onClick={() => onClickAddDestination(false)}
                    className={`${buttonClasses.primary} text-sm py-2 px-3 mr-4`}>
                    {`+ ${t("New Destination")}`}
                </button>}
                <button data-testid="add-existing-destination-button" aria-label="add_existing"
                    onClick={() => onClickAddDestination(true)}
                    className="bg-light-green hover:bg-light-green-hover text-dark-green font-semibold rounded py-2 px-3 text-sm transition-all duration-300">
                    {`+ ${t("Existing Destination")}`}
                </button>
            </div>}
            <div className="mb-3">
                <label className="text-off-black transition-all duration-300">
                    {addExistingDestinations
                        ? t("Select existing destinations to add to the connection")
                        : `${t("Connected destinations")}: ${destinations.length}`}
                </label>
            </div>
            <table className={tableClasses.container}>
                <thead className={tableClasses.header}>
                    <tr>
                        {addExistingDestinations && <th></th>}
                        <th className={tableClasses.headerCell}>{t("Name")}</th>
                        <th className={`${tableClasses.headerCell} flex items-center gap-2`}>
                            {t("Destination")}
                            <span className={tableClasses.sortIcon}><SortingArrows /></span>
                        </th>
                        <th className={`${tableClasses.headerCell} hidden md:table-cell`}>{t("Workspace")}</th>
                        <th className={`${tableClasses.headerCell} hidden md:table-cell`}>{t("Technology")}</th>
                        {!addExistingDestinations && <th></th>}
                    </tr>
                </thead>

                <tbody className="text-sm transition-all duration-300">
                    {addExistingDestinations && (
                        existingDestPhase3.map((destination: Destination, index: number) => {
                            let dest_url: string | URL;
                            try {
                                dest_url = new URL(destination.base_url).hostname;
                            } catch (err) {
                                dest_url = "";
                            }
                            return (
                                <tr key={destination.id} className={tableClasses.row}>
                                    <td className={tableClasses.cell}>
                                        <input
                                            className="accent-dark-green cursor-pointer"
                                            type="checkbox"
                                            value={destination.id}
                                            id={destination.id}
                                            name={destination.name}
                                            checked={existingDestPhase2.map((a: any) => a.id).includes(destination.id)}
                                            onChange={() => onSelectDestination(destination)}
                                        />
                                    </td>
                                    <td className={tableClasses.cell}>
                                        <div>{destination.name}</div>
                                        <div className="md:hidden mt-1 text-xs text-secondary-gray">
                                            {destination.owner.name} · {destination.type.name}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} text-blue-basic`}>
                                        <div className="flex items-center gap-2">
                                            <span className="cursor-pointer hover:underline" onClick={() => onViewDetails(destination.id)}>{dest_url}</span>
                                            {destination.base_url && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(destination.base_url);
                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                            position: toast.POSITION.TOP_RIGHT,
                                                            autoClose: 2000
                                                        });
                                                    }}
                                                    className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                    aria-label="Copy URL to clipboard"
                                                >
                                                    <CopyToClipboard />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.owner.name}</td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.type.name}</td>
                                </tr>
                            );
                        })
                    )}
                    {addExistingDestinations && updatingDestinations && (
                        Array.from({ length: 5 }).map((_, i) => (
                            <tr key={i} className={tableClasses.row}>
                                <td className={tableClasses.cell}><div className={`h-4 w-4 ${skeletonClasses.base}`} /></td>
                                <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                <td className={tableClasses.cell}><div className={`h-4 w-40 ${skeletonClasses.base}`} /></td>
                                <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-28 ${skeletonClasses.base}`} /></td>
                                <td className={`${tableClasses.cell} hidden md:table-cell`}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                            </tr>
                        ))
                    )}
                    {(destlist.length < 1 && !hasOtherPhaseResultsInSearch) ? (
                        <tr>
                            <td colSpan={5} className={`${tableClasses.cell} text-left text-secondary-gray`}>
                                {addExistingDestinations ?
                                    <NoResults
                                        headerMsg={t("No Results Found")}
                                        labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                    />
                                    : t("No Destinations connected to this provider.")}
                            </td>
                        </tr>
                    ) : (
                        (!addExistingDestinations || !updatingDestinations) && destlist.map((destination: Destination) => {
                            let dest_url: string | URL;
                            try {
                                dest_url = new URL(destination.base_url).hostname;
                            } catch (err) {
                                dest_url = "";
                            }
                            return (
                                <tr key={destination.id} className={tableClasses.row}>
                                    {addExistingDestinations && (
                                        <td className={tableClasses.cell}>
                                            <input
                                                className="accent-dark-green cursor-pointer"
                                                type="checkbox"
                                                value={destination.id}
                                                id={destination.id}
                                                name={destination.name}
                                                checked={existingDestPhase1.map((a: any) => a.id).includes(destination.id)}
                                                onChange={() => onSelectDestination(destination)}
                                            />
                                        </td>
                                    )}
                                    <td className={tableClasses.cell}>
                                        <div>{destination.name}</div>
                                        <div className="md:hidden mt-1 text-xs text-secondary-gray">
                                            {destination.owner.name} · {destination.type.name}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} text-blue-basic`}>
                                        <div className="flex items-center gap-2">
                                            <span className="cursor-pointer hover:underline" onClick={() => onViewDetails(destination.id)}>{dest_url}</span>
                                            {destination.base_url && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(destination.base_url);
                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                            position: toast.POSITION.TOP_RIGHT,
                                                            autoClose: 2000
                                                        });
                                                    }}
                                                    className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                    aria-label="Copy URL to clipboard"
                                                >
                                                    <CopyToClipboard />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.owner.name}</td>
                                    <td className={`${tableClasses.cell} hidden md:table-cell`}>{destination.type.name}</td>
                                    {!addExistingDestinations && (
                                        <td className={`${tableClasses.cell} w-20`}>
                                            <div className="flex flex-row items-center justify-end gap-2">
                                                {destinationsWithRules?.has(destination.id) && (
                                                    <span
                                                        title={t("Rule configured")}
                                                        aria-label={t("Rule configured")}
                                                        data-testid="ConnectionDestinationTable-rule-configured"
                                                    >
                                                        <RuleConfiguredIcon />
                                                    </span>
                                                )}
                                                {onOpenRules ? (
                                                    <DestinationActionsMenu
                                                        destinationName={destination.name}
                                                        onOpenRules={() => onOpenRules(destination)}
                                                        onDelete={() => onRemoveDestination(destination)}
                                                    />
                                                ) : (
                                                    <DeleteButton
                                                        onClick={() => onRemoveDestination(destination)}
                                                        className="m-0"
                                                        ariaLabel="Remove destination"
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })

                    )}
                    {(!newConnection && addExistingDestinations) && (
                        <tr className={tableClasses.row}>
                            <td colSpan={5} className={tableClasses.cell}>
                                <button onClick={onAddExistingDestinations}
                                    className={(existingDestPhase1.length > 0 || existingDestPhase2.length > 0)
                                        ? buttonClasses.primary
                                        : buttonClasses.disabled}>
                                    {t("Add Destinations")}
                                </button>
                                <button onClick={() => onClickAddDestination(true)}
                                    className={buttonClasses.secondary}>
                                    {t("Cancel")}
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default ConnectionDestinationTable;
